/**
 * Minhas Pendencias (REF-16) — pagina fiel ao mockup aprovado.
 *
 * Junta tarefas MANUAIS do usuario (CRUD proprio, app `tarefas`) com
 * pendencias AUTOMATICAS agregadas de outras entidades do ERP (assistencias
 * atrasadas, OSEs atrasadas, orcamentos em revisao, contratos pendentes).
 * Visual byte-a-byte do mockup: classes em styles/canonical/minhas-pendencias.css.
 *
 * 3 visoes: Lista (agrupada por prazo), Hoje (foco) e Kanban (por status).
 * Cards clicaveis abrem o TaskModal; o "+ Nova tarefa" abre o SlideOverPanel.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import SlideOverPanel from "../../components/SlideOverPanel";
import AsyncButton from "../../components/AsyncButton";
import DatePicker from "../../components/DatePicker";

import {
  GRUPOS,
  STATUS_INTERNO,
  alternarStatusTarefa,
  arquivarTarefa,
  criarTarefa,
  deletarTarefa,
  desarquivarTarefa,
  getMinhasPendencias,
} from "../../services/minhasPendencias";
import {
  IcAlert,
  IcBolt,
  IcCheck,
  IcChev,
  IcClock,
  IcFilter,
  IcKebab,
  IcPlus,
} from "./icons";
import MenuFlutuante, { ancorarMenu } from "./MenuFlutuante";
import TaskModal from "./TaskModal";
import UserAvatar from "../../components/UserAvatar";

// ---------- Origin badge (system: modulo / manual) ----------
function OriginBadge({ task }) {
  if (task.origin === "system") {
    return (
      <span className="task-origin system">
        <IcBolt /> {task.module}
      </span>
    );
  }
  return (
    <span className="task-origin manual">
      {/* icone de lapis simplificado */}
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z" />
      </svg>
      Manual
    </span>
  );
}

// ---------- Card de tarefa (Lista / Hoje) ----------
function TaskCard({ task, avatar, onAbrir, onToggle, onArquivar, onExcluir }) {
  // pos do menu do kebab ({top,right} | null) — fixed p/ escapar o
  // overflow:hidden do .task (senao o dropdown e cortado na borda do card).
  const [menuPos, setMenuPos] = useState(null);
  const checkCls = task.status === "done" ? "" : `prio-${task.prio}`;
  const dueCls =
    task.dueTone === "danger" ? "due-danger" : task.dueTone === "warn" ? "due-warn" : "";
  const subDone = task.subs.filter((s) => s.done).length;
  const manual = task.origin === "manual";
  // Avatar do RESPONSAVEL real da tarefa (pode ser um colega); fallback = eu.
  const av = task.resp?.nome ? (
    <UserAvatar nome={task.resp.nome} size="sm" />
  ) : (
    avatar
  );

  return (
    <div className={`task ${task.status === "done" ? "is-done" : ""}`}>
      <div className="task__row">
        <div
          className={`task__check ${checkCls}`}
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(task);
          }}
          title={task.origin === "manual" ? "Alternar conclusão" : "Pendencia automática"}
        >
          <IcCheck />
        </div>
        <div className="task__main" style={{ cursor: "pointer" }} onClick={() => onAbrir(task)}>
          <div className="task__title-row">
            <span className="task__title">{task.title}</span>
            <OriginBadge task={task} />
          </div>
          {task.desc ? <div className="task__desc">{task.desc}</div> : null}
          <div className="task__meta">
            {task.due ? (
              <span className={`m-item ${dueCls}`}>
                <IcClock /> {task.due}
              </span>
            ) : null}
            {task.link ? <span className="m-item">{task.link}</span> : null}
            {task.subs.length > 0 ? (
              <span className="subtask-chip">
                <IcChev className="chev" />
                <span className="mini-bar">
                  <span className="f" style={{ width: `${(subDone / task.subs.length) * 100}%` }} />
                </span>{" "}
                {subDone}/{task.subs.length}
              </span>
            ) : null}
          </div>
        </div>
        <div className="task__side">
          {av}
          {manual ? (
            <>
              <button
                className="task__kebab"
                type="button"
                aria-label="Mais ações"
                aria-haspopup="menu"
                aria-expanded={Boolean(menuPos)}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuPos(menuPos ? null : ancorarMenu(e));
                }}
              >
                <IcKebab />
              </button>
              <MenuFlutuante pos={menuPos} onClose={() => setMenuPos(null)}>
                <button
                  type="button"
                  className="tm-status-opt"
                  onClick={() => {
                    setMenuPos(null);
                    onArquivar(task);
                  }}
                >
                  {task.archived ? "Desarquivar" : "Arquivar"}
                </button>
                <button
                  type="button"
                  className="tm-status-opt"
                  style={{ color: "var(--danger-solid, #dc2626)" }}
                  onClick={() => {
                    setMenuPos(null);
                    onExcluir(task);
                  }}
                >
                  Excluir…
                </button>
              </MenuFlutuante>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function GrupoLista({ grupo, items, avatar, onAbrir, onToggle, onArquivar, onExcluir }) {
  if (!items.length) return null;
  return (
    <div className="task-group">
      <div className={`task-group__head ${grupo.tone}`}>
        <div className="tg-title">
          <span className="dot" /> {grupo.label}
        </div>
        <span className="tg-count">{items.length}</span>
        <div className="tg-line" />
      </div>
      {items.map((t) => (
        <TaskCard
          key={t.id}
          task={t}
          avatar={avatar}
          onAbrir={onAbrir}
          onToggle={onToggle}
          onArquivar={onArquivar}
          onExcluir={onExcluir}
        />
      ))}
    </div>
  );
}

function KanbanCard({ task, avatar, onAbrir }) {
  const manual = task.origin === "manual";
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
    data: { task },
    disabled: !manual,
  });
  return (
    <div
      ref={setNodeRef}
      className={`pk-card prio-${task.prio} ${task.status === "done" ? "is-done" : ""} ${isDragging ? "kanban-dragging" : ""}`}
      onClick={() => onAbrir(task)}
      style={manual ? { cursor: "grab" } : undefined}
      {...(manual ? attributes : {})}
      {...(manual ? listeners : {})}
    >
      <div className="pk-card__title">{task.title}</div>
      <OriginBadge task={task} />
      <div className="pk-card__foot">
        <div className="left">
          {task.due ? (
            <span className="m-item" style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)" }}>
              {task.due}
            </span>
          ) : null}
        </div>
        {task.resp?.nome ? <UserAvatar nome={task.resp.nome} size="sm" /> : avatar}
      </div>
    </div>
  );
}

function KanbanColuna({ col, items, avatar, onAbrir }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.id });
  return (
    <div ref={setNodeRef} className={`pk__col ${isOver ? "is-over" : ""}`} data-col={col.id}>
      <div className="pk__col-head">
        <span className="dot" />
        <span className="nm">{col.label}</span>
        <span className="ct">{items.length}</span>
      </div>
      <div className="pk__cards">
        {items.map((t) => (
          <KanbanCard key={t.id} task={t} avatar={avatar} onAbrir={onAbrir} />
        ))}
      </div>
    </div>
  );
}

function KanbanView({ tasks, avatar, onAbrir, onMover }) {
  const cols = [
    { id: "open", label: "A fazer" },
    { id: "doing", label: "Em andamento" },
    { id: "done", label: "Concluida" },
  ];
  // distancia minima de 6px antes de iniciar o drag — preserva o clique.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  function onDragEnd(evento) {
    const { active, over } = evento;
    if (!over) return;
    const task = active.data.current?.task;
    const novoTok = over.id;
    if (task && task.origin === "manual" && task.status !== novoTok) {
      onMover(task, novoTok);
    }
  }

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div className="pk">
        {cols.map((c) => (
          <KanbanColuna
            key={c.id}
            col={c}
            items={tasks.filter((t) => t.status === c.id)}
            avatar={avatar}
            onAbrir={onAbrir}
          />
        ))}
      </div>
    </DndContext>
  );
}

// Arquivadas ficam fora de TODAS as visoes ativas; so aparecem no filtro
// dedicado "arquivadas" (Exibição, no popover de filtros).
const STAT_FILTROS = {
  todas: (t) => !t.archived,
  abertas: (t) => !t.archived && t.status !== "done",
  atrasadas: (t) => !t.archived && t.status !== "done" && t.group === "atrasadas",
  hoje: (t) => !t.archived && t.status !== "done" && t.group === "hoje",
  automaticas: (t) => !t.archived && t.status !== "done" && t.origin === "system",
  concluidas: (t) => !t.archived && t.status === "done",
  arquivadas: (t) => Boolean(t.archived),
};

export default function MinhasPendencias() {
  const { request, usuario } = useAuth();
  const toast = useToast();
  const [payload, setPayload] = useState({ tasks: [], stats: {} });
  const [view, setView] = useState("lista");
  const [statFiltro, setStatFiltro] = useState("abertas");
  const [aberta, setAberta] = useState(null); // tarefa no modal
  const [novaOpen, setNovaOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filtrosOpen, setFiltrosOpen] = useState(false);
  const [filtros, setFiltros] = useState({ prio: [], origem: [] });

  const avatar = useMemo(
    () => (
      <UserAvatar
        src={usuario?.foto_url}
        nome={usuario?.nome}
        email={usuario?.email}
        size="sm"
      />
    ),
    [usuario]
  );

  // IMPORTANTE: nao incluir `toast` nas deps — o objeto do useToast muda de
  // identidade a cada render, o que recriaria `carregar` e re-disparararia o
  // useEffect em loop infinito (martelando o endpoint ate o throttle 429).
  // `request` (AuthContext) e estavel. `toast` e usado via closure.
  const carregar = useCallback(async () => {
    try {
      const data = await getMinhasPendencias(request);
      setPayload(data || { tasks: [], stats: {} });
    } catch (err) {
      toast.error(err?.message || "Erro ao carregar pendencias.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const tasks = payload.tasks || [];
  const stats = payload.stats || {};

  const subLabel = `${stats.abertas || 0} abertas · ${stats.hoje || 0} vencendo hoje`;

  // Filtros extra (prioridade / origem) — combinam com o filtro de stat.
  const passaFiltros = (t) => {
    if (filtros.prio.length && !filtros.prio.includes(t.prio)) return false;
    if (filtros.origem.length && !filtros.origem.includes(t.origin)) return false;
    return true;
  };
  const filtrosAtivos = filtros.prio.length + filtros.origem.length;
  const tasksBase = tasks.filter(passaFiltros);
  // Lista: aplica o filtro do stat ativo, depois agrupa por prazo.
  const tasksFiltradas = tasksBase.filter(STAT_FILTROS[statFiltro] || (() => true));

  async function moverTask(task, novoTok) {
    // atualizacao otimista + persiste
    setPayload((p) => ({
      ...p,
      tasks: p.tasks.map((t) => (t.id === task.id ? { ...t, status: novoTok } : t)),
    }));
    try {
      await alternarStatusTarefa(request, task.id, STATUS_INTERNO[novoTok]);
      await carregar();
    } catch (err) {
      toast.error(err?.message || "Erro ao mover tarefa.");
      await carregar();
    }
  }

  async function toggleTask(task) {
    if (task.origin !== "manual") {
      setAberta(task);
      return;
    }
    try {
      await alternarStatusTarefa(request, task.id);
      await carregar();
    } catch (err) {
      toast.error(err?.message || "Erro ao alternar status.");
    }
  }

  async function arquivarTask(task) {
    try {
      await (task.archived ? desarquivarTarefa : arquivarTarefa)(request, task.id);
      toast.success(task.archived ? "Tarefa desarquivada." : "Tarefa arquivada.");
      await carregar();
    } catch (err) {
      toast.error(err?.message || "Erro ao arquivar tarefa.");
    }
  }

  async function excluirTask(task) {
    if (!window.confirm(`Mover a tarefa "${task.title}" para a lixeira? Ela some da lista e é apagada em 30 dias.`)) {
      return;
    }
    try {
      await deletarTarefa(request, task.id);
      toast.success("Tarefa movida para a lixeira (30 dias).");
      await carregar();
    } catch (err) {
      toast.error(err?.message || "Erro ao excluir tarefa.");
    }
  }

  const STAT_CARDS = [
    { id: "abertas", lbl: "Abertas", val: stats.abertas || 0, sub: `${stats.automaticas || 0} automáticas · ${stats.manuais || 0} manuais`, cls: "" },
    { id: "atrasadas", lbl: "Atrasadas", val: stats.atrasadas || 0, sub: "requer atenção", cls: "danger", ic: <IcAlert /> },
    { id: "hoje", lbl: "Vencem hoje", val: stats.hoje || 0, sub: "até 18h", cls: "warning", ic: <IcClock /> },
    { id: "automaticas", lbl: "Automáticas", val: stats.automaticas || 0, sub: "geradas pelo sistema", cls: "accent", ic: <IcBolt /> },
    { id: "concluidas", lbl: "Concluídas", val: stats.concluidas || 0, sub: "esta semana", cls: "success", ic: <IcCheck /> },
  ];

  const hojeItems = tasksBase.filter(
    (t) => !t.archived && (t.group === "atrasadas" || t.group === "hoje") && t.status !== "done"
  );

  return (
    <section className="screen is-active">
      {/* Cabecalho (REF#16c/16d: a `.screen` fica fora do `.page` padronizado
          (sem padding-top), entao damos respiro explicito no topo). */}
      <div className="page-header" style={{ marginTop: 28, marginBottom: 18 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span>
            <span className="crumb-sep">/</span>
            <span className="active">Agenda</span>
          </div>
          <h1 className="page-title">Agenda</h1>
          <div className="page-sub">{loading ? "Carregando…" : subLabel}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div className="tabchip-group">
            {[
              { id: "hoje", label: "Hoje" },
              { id: "lista", label: "Lista" },
              { id: "kanban", label: "Kanban" },
            ].map((t) => (
              <div
                key={t.id}
                className={`tabchip ${view === t.id ? "active" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => setView(t.id)}
              >
                {t.label}
              </div>
            ))}
          </div>
          <div className="pend-filtros">
            <button
              className="btn btn--ghost"
              type="button"
              onClick={() => setFiltrosOpen((v) => !v)}
              aria-expanded={filtrosOpen}
            >
              <IcFilter /> Filtros{filtrosAtivos ? ` (${filtrosAtivos})` : ""}
            </button>
            {filtrosOpen ? (
              <>
                <div style={{ position: "fixed", inset: 0, zIndex: 29 }} onMouseDown={() => setFiltrosOpen(false)} />
                <div className="pend-filtros__pop">
                  <div className="pend-filtros__grp">
                    <span className="pend-filtros__lbl">Prioridade</span>
                    <div className="pend-filtros__chips">
                      {[
                        { id: "hi", label: "Alta" },
                        { id: "mid", label: "Media" },
                        { id: "low", label: "Baixa" },
                      ].map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className={`pend-filtros__chip ${filtros.prio.includes(p.id) ? "is-on" : ""}`}
                          onClick={() =>
                            setFiltros((f) => ({
                              ...f,
                              prio: f.prio.includes(p.id) ? f.prio.filter((x) => x !== p.id) : [...f.prio, p.id],
                            }))
                          }
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="pend-filtros__grp">
                    <span className="pend-filtros__lbl">Origem</span>
                    <div className="pend-filtros__chips">
                      {[
                        { id: "manual", label: "Manual" },
                        { id: "system", label: "Automática" },
                      ].map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          className={`pend-filtros__chip ${filtros.origem.includes(o.id) ? "is-on" : ""}`}
                          onClick={() =>
                            setFiltros((f) => ({
                              ...f,
                              origem: f.origem.includes(o.id) ? f.origem.filter((x) => x !== o.id) : [...f.origem, o.id],
                            }))
                          }
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="pend-filtros__grp">
                    <span className="pend-filtros__lbl">Exibição</span>
                    <div className="pend-filtros__chips">
                      <button
                        type="button"
                        className={`pend-filtros__chip ${statFiltro === "todas" ? "is-on" : ""}`}
                        onClick={() => { setStatFiltro("todas"); setView("lista"); }}
                      >
                        Mostrar todas (incl. concluídas)
                      </button>
                      <button
                        type="button"
                        className={`pend-filtros__chip ${statFiltro === "arquivadas" ? "is-on" : ""}`}
                        onClick={() => { setStatFiltro("arquivadas"); setView("lista"); }}
                      >
                        Arquivadas ({stats.arquivadas || 0})
                      </button>
                    </div>
                  </div>
                  <div className="pend-filtros__foot">
                    <button
                      className="btn btn--ghost btn--sm"
                      type="button"
                      onClick={() => { setFiltros({ prio: [], origem: [] }); setStatFiltro("abertas"); }}
                    >
                      Limpar
                    </button>
                    <button className="btn btn--primary btn--sm" type="button" onClick={() => setFiltrosOpen(false)}>
                      Aplicar
                    </button>
                  </div>
                </div>
              </>
            ) : null}
          </div>
          <button className="btn btn--primary" type="button" onClick={() => setNovaOpen(true)}>
            <IcPlus /> Nova tarefa
          </button>
        </div>
      </div>

      {/* Stats clicaveis */}
      <div className="pend-stats">
        {STAT_CARDS.map((s) => (
          <div
            key={s.id}
            className={`pend-stat ${s.cls} ${statFiltro === s.id ? "is-active" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => {
              setStatFiltro(s.id);
              setView("lista");
            }}
          >
            <div className="pend-stat__lbl">
              {s.ic || null} {s.lbl}
            </div>
            <div className="pend-stat__val">{s.val}</div>
            <div className="pend-stat__sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Views */}
      <div className="pend-views">
        {view === "lista" && (
          <div className="pend-view is-active">
            {tasksFiltradas.length === 0 ? (
              <div className="empty">
                <p className="title">Nada por aqui 🎉</p>
                <p className="msg">Nenhuma pendencia com esse filtro.</p>
              </div>
            ) : (
              GRUPOS.map((g) => (
                <GrupoLista
                  key={g.id}
                  grupo={g}
                  items={tasksFiltradas.filter((t) => t.group === g.id)}
                  avatar={avatar}
                  onAbrir={setAberta}
                  onToggle={toggleTask}
                  onArquivar={arquivarTask}
                  onExcluir={excluirTask}
                />
              ))
            )}
          </div>
        )}

        {view === "hoje" && (
          <div className="pend-view is-active">
            {hojeItems.length === 0 ? (
              <div className="empty">
                <p className="title">Nada para hoje 🎉</p>
                <p className="msg">Sem pendencias vencendo hoje ou atrasadas.</p>
              </div>
            ) : (
              <div className="task-group">
                <div className="task-group__head danger">
                  <div className="tg-title">
                    <span className="dot" /> Foco de hoje
                  </div>
                  <span className="tg-count">{hojeItems.length}</span>
                  <div className="tg-line" />
                </div>
                {hojeItems.map((t) => (
                  <TaskCard
                    key={t.id}
                    task={t}
                    avatar={avatar}
                    onAbrir={setAberta}
                    onToggle={toggleTask}
                    onArquivar={arquivarTask}
                    onExcluir={excluirTask}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {view === "kanban" && (
          <div className="pend-view is-active">
            <KanbanView
              tasks={tasksBase.filter((t) => !t.archived)}
              avatar={avatar}
              onAbrir={setAberta}
              onMover={moverTask}
            />
          </div>
        )}
      </div>

      {/* Modal de detalhe */}
      <TaskModal
        task={aberta}
        avatar={avatar}
        onClose={() => setAberta(null)}
        onChange={carregar}
      />

      {/* Slide-over Nova tarefa */}
      <NovaTarefaPanel
        open={novaOpen}
        onClose={() => setNovaOpen(false)}
        onCriou={async () => {
          setNovaOpen(false);
          // REF#16c: garante que a tarefa recem-criada apareca na tela. Sem
          // prazo, ela cai no grupo "Sem prazo" (que so existe na visao Lista,
          // nao em "Hoje"); e se o usuario estava num filtro de stat especifico
          // ela poderia ficar oculta. Voltamos para Lista + "abertas".
          setView("lista");
          setStatFiltro("abertas");
          await carregar();
        }}
      />
    </section>
  );
}

// ============================================================
// Slide-over: Nova tarefa
// ============================================================
function NovaTarefaPanel({ open, onClose, onCriou }) {
  const { request } = useAuth();
  const toast = useToast();
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [prioridade, setPrioridade] = useState("MEDIA");
  const [prazoDate, setPrazoDate] = useState(null);
  const [prazoOpen, setPrazoOpen] = useState(false);
  const [subs, setSubs] = useState([]);
  const prazoBtnRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTitulo("");
      setDescricao("");
      setPrioridade("MEDIA");
      setPrazoDate(null);
      setPrazoOpen(false);
      setSubs([]);
    }
  }, [open]);

  const prioOpts = [
    { id: "BAIXA", cls: "low", label: "Baixa" },
    { id: "MEDIA", cls: "mid", label: "Media" },
    { id: "ALTA", cls: "hi", label: "Alta" },
  ];

  async function salvar() {
    if (!titulo.trim()) {
      toast.error("Informe o título da tarefa.");
      throw new Error("título vazio");
    }
    const dados = {
      titulo: titulo.trim(),
      descricao: descricao.trim(),
      prioridade,
      subtarefas: subs.filter((s) => s.titulo.trim()).map((s) => ({ titulo: s.titulo.trim(), prazo: s.prazo || null })),
    };
    if (prazoDate) dados.prazo = prazoDate.toISOString();
    await criarTarefa(request, dados);
    await onCriou();
  }

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title="Nova tarefa"
      subtitle="Crie uma pendencia manual com subtarefas e prazo"
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn--ghost" type="button" onClick={onClose}>
            Cancelar
          </button>
          <AsyncButton variant="primary" onClick={salvar} successMessage="Tarefa criada." silentError>
            <IcPlus /> Criar tarefa
          </AsyncButton>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span className="tm-prop__lbl" style={{ margin: 0 }}>Título</span>
          <input
            className="input"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ex.: Ligar para o fornecedor"
            autoFocus
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span className="tm-prop__lbl" style={{ margin: 0 }}>Descrição</span>
          <textarea
            className="input"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            rows={3}
            placeholder="Detalhes (opcional)"
          />
        </label>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span className="tm-prop__lbl" style={{ margin: 0 }}>Prioridade</span>
          <div className="prio-pick" id="prioPick">
            {prioOpts.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`prio-opt ${p.cls} ${prioridade === p.id ? "is-on" : ""}`}
                onClick={() => setPrioridade(p.id)}
              >
                <span className="d" /> {p.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, position: "relative" }}>
          <span className="tm-prop__lbl" style={{ margin: 0 }}>Prazo</span>
          <button
            ref={prazoBtnRef}
            type="button"
            className="tm-prop__edit"
            style={{ margin: 0, border: "1px solid var(--border)", padding: "9px 12px" }}
            onClick={() => setPrazoOpen((v) => !v)}
          >
            {prazoDate ? prazoDate.toLocaleDateString("pt-BR") : "Definir prazo"}
          </button>
          {prazoOpen ? (
            <DatePicker
              value={prazoDate}
              onSelect={(d) => setPrazoDate(d)}
              onClose={() => setPrazoOpen(false)}
              anchorRef={prazoBtnRef}
            />
          ) : null}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span className="tm-prop__lbl" style={{ margin: 0 }}>Subtarefas</span>
          {subs.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 6 }}>
              <input
                className="input"
                style={{ flex: 1 }}
                value={s.titulo}
                placeholder="Subtarefa"
                onChange={(e) =>
                  setSubs((arr) => arr.map((x, j) => (j === i ? { ...x, titulo: e.target.value } : x)))
                }
              />
              <button
                className="btn btn--ghost"
                type="button"
                onClick={() => setSubs((arr) => arr.filter((_, j) => j !== i))}
                aria-label="Remover subtarefa"
              >
                ×
              </button>
            </div>
          ))}
          <button
            className="subtask-add"
            type="button"
            onClick={() => setSubs((arr) => [...arr, { titulo: "", prazo: "" }])}
          >
            <IcPlus /> Adicionar subtarefa
          </button>
        </div>
      </div>
    </SlideOverPanel>
  );
}
